import * as React from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';

// Google's shared demo id: the default Google look, but it unlocks Advanced
// Markers. Real environments set REACT_APP_GOOGLE_MAPS_MAP_ID to the KOOB
// cloud style (see README).
export const DEMO_MAP_ID = 'DEMO_MAP_ID';

const MapIdContext = React.createContext<string>(DEMO_MAP_ID);

export const useKoobMapId = () => React.useContext(MapIdContext);

// Google renders info windows, autocomplete dropdowns and clusters outside
// React, so their look can only be set with global selectors.
const GLOBAL_CSS = `
/* Lets popups size themselves against the map's height (cqh units). */
.gm-style {
  container-type: size;
}
/* KoobMapPopup caps the height and pans the popup into view itself; Google's
   cap (the room above the pin) would cut the card off. */
.gm-style .gm-style-iw-c {
  max-height: none !important;
  padding: 0 !important;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(11, 18, 32, 0.18), 0 1px 3px rgba(11, 18, 32, 0.12);
}
.gm-style .gm-style-iw-d {
  max-height: none !important;
  overflow: hidden !important;
  padding: 0 !important;
}
.gm-style .gm-style-iw-tc::after {
  box-shadow: none;
}
.koob-map-cluster {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid #ffffff;
  border-radius: 9999px;
  background: #f5700a;
  color: #ffffff;
  font: 600 13px/1 Inter, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  box-shadow: 0 2px 6px rgba(11, 18, 32, 0.3);
  transform: translateY(50%);
  cursor: pointer;
  transition: transform 150ms ease;
}
/* MUTED_PIN_COLOR from @koob/map-marker */
.koob-map-cluster--muted {
  background: #9ca3af;
}
.koob-map-cluster:hover {
  transform: translateY(50%) scale(1.08);
}
.pac-container {
  margin-top: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(11, 18, 32, 0.12);
  font-family: inherit;
}
.pac-container::after {
  margin: 0 8px 4px;
}
.pac-item {
  padding: 6px 12px;
  border-top-color: #f3f4f6;
  cursor: pointer;
}
`;

type Props = {
  apiKey: string;
  /** Cloud-styled map id; falls back to DEMO_MAP_ID. */
  mapId?: string;
  language?: string;
  children: React.ReactNode;
};

/**
 * Loads the Maps JavaScript API once for the whole app and hands every
 * KoobMap the shared KOOB map id.
 */
export function KoobMapsProvider({ apiKey, mapId, language, children }: Props) {
  return (
    <APIProvider apiKey={apiKey} language={language}>
      <MapIdContext.Provider value={mapId || DEMO_MAP_ID}>
        <style>{GLOBAL_CSS}</style>
        {children}
      </MapIdContext.Provider>
    </APIProvider>
  );
}
